import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Chip,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  Hidden,
  IconButton,
  Collapse,
  Checkbox,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { CSVLink } from 'react-csv';
import GetAppIcon from '@material-ui/icons/GetApp';
import FilterListIcon from '@material-ui/icons/FilterList';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import CancelIcon from '@material-ui/icons/Cancel';
import JSONPretty from 'react-json-pretty';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: '#fff',
    borderRadius: 8,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  tableContainer: {
    marginTop: theme.spacing(1),
    overflowX: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  statusActive: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  statusClosed: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
  },
  statusFailed: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  exportButton: {
    marginLeft: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  dialogContent: {
    minWidth: 'auto',
    [theme.breakpoints.up('sm')]: {
      minWidth: 500,
    },
  },
  detailRow: {
    marginBottom: theme.spacing(1),
  },
  tabs: {
    marginBottom: theme.spacing(1),
    '& .MuiTab-root': {
      minWidth: 'auto',
      padding: '6px 12px',
      fontSize: '0.75rem',
      [theme.breakpoints.up('sm')]: {
        minWidth: 160,
        padding: '6px 24px',
        fontSize: '0.875rem',
      },
    },
  },
  // Mobile order card styles
  mobileOrderCard: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  mobileOrderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: '1px solid #eee',
  },
  mobileOrderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  mobileOrderLabel: {
    color: '#666',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  mobileOrderValue: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textAlign: 'right',
    maxWidth: '60%',
    wordBreak: 'break-word',
  },
  mobileScriptName: {
    fontWeight: 600,
    fontSize: '0.875rem',
    maxWidth: '70%',
    wordBreak: 'break-word',
  },
  filterField: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      marginBottom: 0,
    },
  },
  mobileActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  paginationMobile: {
    '& .MuiTablePagination-toolbar': {
      flexWrap: 'wrap',
      justifyContent: 'center',
      padding: theme.spacing(1),
    },
    '& .MuiTablePagination-spacer': {
      display: 'none',
    },
    '& .MuiTablePagination-selectRoot': {
      marginRight: theme.spacing(1),
    },
  },
  bulkActionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#e3f2fd',
    padding: theme.spacing(1, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  bulkActionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  exitButton: {
    backgroundColor: '#fbc02d',
    color: '#000',
    '&:hover': {
      backgroundColor: '#f9a825',
    },
  },
  reactivateButton: {
    backgroundColor: '#4caf50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#388e3c',
    },
  },
  cancelButton: {
    backgroundColor: '#f44336',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  statusReactivated: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  statusCancelled: {
    backgroundColor: '#fce4ec',
    color: '#c2185b',
  },
}));

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#1a1a2e',
    color: '#fff',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    padding: '12px 8px',
  },
}))(TableCell);

function Orders() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filters, setFilters] = useState({
    catalog: '',
    provider: '',
    status: '',
    user: '',
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use V2 APIs for role-based filtering (Admin sees all, Users see own)
      const [allOrders, active] = await Promise.all([
        apiService.getOrdersV2(),
        apiService.getActiveOrdersV2(),
      ]);
      setOrders(allOrders);
      setActiveOrders(active);
      setSelectedOrders([]); // Clear selection on refresh
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const currentOrders = tabValue === 0 ? activeOrders : orders;

  const filteredOrders = currentOrders.filter((order) => {
    if (filters.catalog && !order.catalog_name?.toLowerCase().includes(filters.catalog.toLowerCase())) {
      return false;
    }
    if (filters.provider && order.app_provider !== filters.provider) {
      return false;
    }
    if (filters.status && order.status !== filters.status) {
      return false;
    }
    if (filters.user && !order.user_dp_id?.toLowerCase().includes(filters.user.toLowerCase())) {
      return false;
    }
    return true;
  });

  const handleFilterChange = (field) => (event) => {
    setFilters({ ...filters, [field]: event.target.value });
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  // Bulk selection handlers
  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = () => {
    const currentPageOrderIds = filteredOrders
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((o) => o.id);

    const allSelected = currentPageOrderIds.every((id) => selectedOrders.includes(id));

    if (allSelected) {
      setSelectedOrders((prev) => prev.filter((id) => !currentPageOrderIds.includes(id)));
    } else {
      setSelectedOrders((prev) => [...new Set([...prev, ...currentPageOrderIds])]);
    }
  };

  const clearSelection = () => {
    setSelectedOrders([]);
  };

  // Bulk action handlers
  const handleBulkExit = async () => {
    if (selectedOrders.length === 0) return;

    const activeSelected = orders.filter(
      (o) => selectedOrders.includes(o.id) && o.status === 'ACTIVE'
    );

    if (activeSelected.length === 0) {
      setSnackbar({ open: true, message: 'No active orders selected for exit', severity: 'warning' });
      return;
    }

    if (!window.confirm(`Are you sure you want to exit ${activeSelected.length} active order(s)?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const result = await apiService.bulkExitOrders(activeSelected.map((o) => o.id));
      setSnackbar({
        open: true,
        message: `Exited ${result.exited?.length || 0} orders, ${result.failed?.length || 0} failed`,
        severity: result.exited?.length > 0 ? 'success' : 'error',
      });
      clearSelection();
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Bulk exit failed', severity: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReactivate = async () => {
    if (selectedOrders.length === 0) return;

    // Allow reactivation from any status except ACTIVE and CLOSED
    const reactivatableSelected = orders.filter(
      (o) => selectedOrders.includes(o.id) && !['ACTIVE', 'CLOSED'].includes(o.status)
    );

    if (reactivatableSelected.length === 0) {
      setSnackbar({ open: true, message: 'No orders selected for reactivation (orders may already be ACTIVE or CLOSED)', severity: 'warning' });
      return;
    }

    if (!window.confirm(`Are you sure you want to set ${reactivatableSelected.length} order(s) to ACTIVE status?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const result = await apiService.bulkReactivateOrders(reactivatableSelected.map((o) => o.id));
      setSnackbar({
        open: true,
        message: `Reactivated ${result.reactivated?.length || 0} orders, ${result.failed?.length || 0} failed`,
        severity: result.reactivated?.length > 0 ? 'success' : 'error',
      });
      clearSelection();
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Bulk reactivate failed', severity: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkCancel = async () => {
    if (selectedOrders.length === 0) return;

    const cancellableSelected = orders.filter(
      (o) => selectedOrders.includes(o.id) && !['CLOSED', 'CANCELLED'].includes(o.status)
    );

    if (cancellableSelected.length === 0) {
      setSnackbar({ open: true, message: 'No cancellable orders selected', severity: 'warning' });
      return;
    }

    if (!window.confirm(`Are you sure you want to cancel ${cancellableSelected.length} order(s)? This will NOT execute exit trades.`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const result = await apiService.bulkCancelOrders(cancellableSelected.map((o) => o.id));
      setSnackbar({
        open: true,
        message: `Cancelled ${result.cancelled?.length || 0} orders, ${result.failed?.length || 0} failed`,
        severity: result.cancelled?.length > 0 ? 'success' : 'error',
      });
      clearSelection();
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Bulk cancel failed', severity: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusChip = (status) => {
    const chipStyle = { height: 22, fontSize: '0.7rem' };
    switch (status) {
      case 'ACTIVE':
        return <Chip size="small" label="Active" className={classes.statusActive} style={chipStyle} />;
      case 'CLOSED':
        return <Chip size="small" label="Closed" className={classes.statusClosed} style={chipStyle} />;
      case 'FAILED':
        return <Chip size="small" label="Failed" className={classes.statusFailed} style={chipStyle} />;
      case 'REACTIVATED':
        return <Chip size="small" label="Reactivated" className={classes.statusReactivated} style={chipStyle} />;
      case 'CANCELLED':
        return <Chip size="small" label="Cancelled" className={classes.statusCancelled} style={chipStyle} />;
      default:
        return <Chip size="small" label={status || 'N/A'} style={chipStyle} />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCSVData = () => {
    return filteredOrders.map((order) => {
      const { details_json, ...rest } = order;
      return rest;
    });
  };

  const uniqueProviders = [...new Set(orders.map((o) => o.app_provider).filter(Boolean))];
  const uniqueStatuses = [...new Set(orders.map((o) => o.status).filter(Boolean))];

  // Mobile order card component
  const MobileOrderCard = ({ order }) => (
    <Card className={classes.mobileOrderCard}>
      <Box className={classes.mobileOrderHeader}>
        <Box display="flex" alignItems="center">
          <Checkbox
            checked={selectedOrders.includes(order.id)}
            onChange={() => handleSelectOrder(order.id)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            color="primary"
          />
          <Typography className={classes.mobileScriptName} onClick={() => handleRowClick(order)} style={{ cursor: 'pointer' }}>
            {order.script_name}
          </Typography>
        </Box>
        {getStatusChip(order.status)}
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Provider / User</Typography>
        <Typography className={classes.mobileOrderValue}>
          {order.app_provider} / {order.user_dp_id}
        </Typography>
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Catalog</Typography>
        <Typography className={classes.mobileOrderValue}>{order.catalog_name}</Typography>
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Side</Typography>
        <Chip
          size="small"
          label={order.transaction_side}
          style={{
            backgroundColor: order.transaction_side === 'LONG' ? '#e8f5e9' : '#ffebee',
            color: order.transaction_side === 'LONG' ? '#2e7d32' : '#c62828',
            height: 20,
            fontSize: '0.7rem',
          }}
        />
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Entry / Exit</Typography>
        <Typography className={classes.mobileOrderValue}>
          {order.entry_price || '-'} / {order.exit_price || '-'}
        </Typography>
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Time</Typography>
        <Typography className={classes.mobileOrderValue}>{formatDate(order.created_at)}</Typography>
      </Box>
    </Card>
  );

  if (loading) {
    return (
      <Layout title="Orders" onRefresh={fetchData}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Orders" onRefresh={fetchData}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
        variant="fullWidth"
      >
        <Tab label={`Active (${activeOrders.length})`} />
        <Tab label={`All (${orders.length})`} />
      </Tabs>

      {/* Bulk Action Bar */}
      {selectedOrders.length > 0 && (
        <Paper className={classes.bulkActionBar}>
          <Typography variant="body2">
            <strong>{selectedOrders.length}</strong> order(s) selected
          </Typography>
          <Box className={classes.bulkActionButtons}>
            <Button
              variant="contained"
              size="small"
              className={classes.exitButton}
              startIcon={bulkActionLoading ? <CircularProgress size={16} /> : <ExitToAppIcon />}
              onClick={handleBulkExit}
              disabled={bulkActionLoading}
            >
              Exit
            </Button>
            <Button
              variant="contained"
              size="small"
              className={classes.reactivateButton}
              startIcon={bulkActionLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleBulkReactivate}
              disabled={bulkActionLoading}
            >
              Reactivate
            </Button>
            <Button
              variant="contained"
              size="small"
              className={classes.cancelButton}
              startIcon={bulkActionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
              onClick={handleBulkCancel}
              disabled={bulkActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={clearSelection}
              disabled={bulkActionLoading}
            >
              Clear
            </Button>
          </Box>
        </Paper>
      )}

      {/* Filters */}
      <Paper className={classes.filterContainer}>
        {/* Mobile filter toggle */}
        <Box className={classes.filterHeader}>
          <Box display="flex" alignItems="center">
            <FilterListIcon style={{ marginRight: 8, color: '#666' }} />
            <Typography variant="subtitle2">Filters</Typography>
          </Box>
          <IconButton size="small" onClick={() => setFiltersExpanded(!filtersExpanded)}>
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Desktop filters - always visible */}
        <Hidden xsDown>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Search Catalog"
                variant="outlined"
                value={filters.catalog}
                onChange={handleFilterChange('catalog')}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                select
                label="Provider"
                variant="outlined"
                value={filters.provider}
                onChange={handleFilterChange('provider')}
              >
                <MenuItem value="">All</MenuItem>
                {uniqueProviders.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                variant="outlined"
                value={filters.status}
                onChange={handleFilterChange('status')}
              >
                <MenuItem value="">All</MenuItem>
                {uniqueStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Search User"
                variant="outlined"
                value={filters.user}
                onChange={handleFilterChange('user')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box display="flex" justifyContent="flex-end">
                <CSVLink data={getCSVData()} filename="mgm-orders.csv" style={{ textDecoration: 'none' }}>
                  <Button variant="outlined" startIcon={<GetAppIcon />}>
                    Export CSV
                  </Button>
                </CSVLink>
              </Box>
            </Grid>
          </Grid>
        </Hidden>

        {/* Mobile filters - collapsible */}
        <Hidden smUp>
          <Collapse in={filtersExpanded}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Catalog"
                  variant="outlined"
                  value={filters.catalog}
                  onChange={handleFilterChange('catalog')}
                  className={classes.filterField}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="User"
                  variant="outlined"
                  value={filters.user}
                  onChange={handleFilterChange('user')}
                  className={classes.filterField}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Provider"
                  variant="outlined"
                  value={filters.provider}
                  onChange={handleFilterChange('provider')}
                  className={classes.filterField}
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueProviders.map((provider) => (
                    <MenuItem key={provider} value={provider}>
                      {provider}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Status"
                  variant="outlined"
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  className={classes.filterField}
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Collapse>
          <Box className={classes.mobileActions}>
            <Typography variant="caption" color="textSecondary">
              {filteredOrders.length} orders
            </Typography>
            <CSVLink data={getCSVData()} filename="mgm-orders.csv" style={{ textDecoration: 'none' }}>
              <Button variant="outlined" size="small" startIcon={<GetAppIcon />}>
                Export
              </Button>
            </CSVLink>
          </Box>
        </Hidden>
      </Paper>

      {/* Mobile Orders - Card View */}
      <Hidden smUp>
        <Box className={classes.tableContainer}>
          {filteredOrders
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((order, index) => (
              <MobileOrderCard key={order.id || index} order={order} />
            ))}
          <TablePagination
            rowsPerPageOptions={[10, 15, 25]}
            component="div"
            count={filteredOrders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            className={classes.paginationMobile}
            labelRowsPerPage=""
          />
        </Box>
      </Hidden>

      {/* Desktop Orders Table */}
      <Hidden xsDown>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedOrders.length > 0 &&
                      selectedOrders.length < filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length
                    }
                    checked={
                      filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length > 0 &&
                      filteredOrders
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .every((o) => selectedOrders.includes(o.id))
                    }
                    onChange={handleSelectAllOrders}
                    style={{ color: '#fff' }}
                  />
                </StyledTableCell>
                <StyledTableCell>Provider</StyledTableCell>
                <StyledTableCell>User</StyledTableCell>
                <StyledTableCell>Catalog</StyledTableCell>
                <StyledTableCell>Script</StyledTableCell>
                <StyledTableCell>Instrument</StyledTableCell>
                <StyledTableCell>Side</StyledTableCell>
                <StyledTableCell>Entry</StyledTableCell>
                <StyledTableCell>Exit</StyledTableCell>
                <StyledTableCell>Created</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order, index) => (
                  <TableRow
                    key={order.id || index}
                    hover
                    selected={selectedOrders.includes(order.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{order.app_provider}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{order.user_dp_id}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{order.catalog_name}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{order.script_name}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{order.instrument_type}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>
                      <Chip
                        size="small"
                        label={order.transaction_side}
                        style={{
                          backgroundColor: order.transaction_side === 'LONG' ? '#e8f5e9' : '#ffebee',
                          color: order.transaction_side === 'LONG' ? '#2e7d32' : '#c62828',
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{order.entry_price || '-'}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{order.exit_price || '-'}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{formatDate(order.created_at)}</TableCell>
                    <TableCell onClick={() => handleRowClick(order)}>{getStatusChip(order.status)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 15, 25, 50]}
            component="div"
            count={filteredOrders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Hidden>

      {/* Order Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Details - {selectedOrder?.user_dp_id}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {selectedOrder && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Order ID</Typography>
                  <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                    {selectedOrder.mgm_order_id}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  {getStatusChip(selectedOrder.status)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Entry Price</Typography>
                  <Typography>{selectedOrder.entry_price || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Exit Price</Typography>
                  <Typography>{selectedOrder.exit_price || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Quantity</Typography>
                  <Typography>{selectedOrder.quantity}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Default Qty</Typography>
                  <Typography>{selectedOrder.default_qty}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Message</Typography>
                  <Typography variant="body2">{selectedOrder.message || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Details JSON</Typography>
                  <Box style={{ maxHeight: 200, overflow: 'auto', backgroundColor: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                    <JSONPretty data={selectedOrder.details_json} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default Orders;
